/**
 * Authentication Service
 *
 * Handles all authentication-related operations including login, logout,
 * password reset, and token refresh. Manages authentication state by
 * coordinating with TokenService and UserService.
 *
 * @example
 * ```typescript
 * // In a login component
 * constructor(private authService: AuthService, private router: Router) {}
 *
 * login() {
 *   const credentials = { email: 'admin@blueride.com', password: 'password123' };
 *   this.authService.login(credentials).subscribe({
 *     next: (response) => {
 *       console.log('Login successful');
 *       this.router.navigate(['/dashboard']);
 *     },
 *     error: (error) => {
 *       console.error('Login failed:', error);
 *     }
 *   });
 * }
 * ```
 */

import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { tap, finalize, switchMap } from 'rxjs/operators';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  RefreshTokenRequest,
  RefreshTokenResponse
} from '../models';
import { TokenService } from './token.service';
import { UserService } from './user.service';
import { SecurityService } from './security.service';
import { SecurityLogService } from './security-log.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  /**
   * Base API URL for authentication endpoints
   */
  private readonly apiUrl = `${environment.apiBaseUrl}/auth`;

  /**
   * Subscription for auto token refresh
   */
  private refreshTokenSubscription?: Subscription;

  /**
   * BehaviorSubject tracking authentication state
   */
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  /**
   * Observable stream of authentication state
   * Components should subscribe to this for reactive auth state updates
   */
  readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private userService: UserService,
    private securityService: SecurityService,
    private securityLog: SecurityLogService
  ) {
    this.checkTokenOnInit();
  }

  ngOnDestroy(): void {
    this.stopTokenRefresh();
  }

  /**
   * Checks token expiration on service initialization
   * Auto-logs out if token is expired
   */
  private checkTokenOnInit(): void {
    const token = this.tokenService.getToken();
    if (token && this.securityService.isTokenExpired(token)) {
      this.securityLog.logTokenExpired();
      this.clearAuthState();
    } else if (token) {
      this.isAuthenticatedSubject.next(true);
    }
  }

  /**
   * Starts automatic token refresh
   * Refreshes token before it expires
   */
  private startTokenRefresh(): void {
    this.stopTokenRefresh();

    const token = this.tokenService.getToken();
    if (!token) {
      return;
    }

    const expiration = this.securityService.getTokenExpiration(token);
    if (!expiration) {
      return;
    }

    // Refresh 5 minutes before expiration
    const refreshTime = expiration.getTime() - Date.now() - (5 * 60 * 1000);

    if (refreshTime > 0) {
      this.refreshTokenSubscription = timer(refreshTime).pipe(
        switchMap(() => this.refreshToken())
      ).subscribe({
        next: () => {
          this.securityLog.logAuthEvent('refresh');
        },
        error: (error) => {
          console.error('Auto token refresh failed:', error);
          this.clearAuthState();
        }
      });
    }
  }

  /**
   * Stops automatic token refresh
   */
  private stopTokenRefresh(): void {
    if (this.refreshTokenSubscription) {
      this.refreshTokenSubscription.unsubscribe();
      this.refreshTokenSubscription = undefined;
    }
  }

  /**
   * Clears authentication state (internal use)
   */
  private clearAuthState(): void {
    this.tokenService.clearToken();
    this.userService.clearCurrentUser();
    this.stopTokenRefresh();
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Forces logout without API call
   * Used by error interceptor when 401 is received
   */
  forceLogout(): void {
    this.clearAuthState();
  }

  /**
   * Authenticates a user with email and password
   *
   * On successful login:
   * - Stores the authentication token
   * - Sets the current user in UserService
   *
   * @param credentials - User's login credentials
   * @returns Observable containing the login response
   *
   * @example
   * ```typescript
   * this.authService.login({ email: 'admin@blueride.com', password: 'pass123' })
   *   .subscribe({
   *     next: (response) => console.log('Logged in as:', response.data.user.name),
   *     error: (err) => console.error('Login failed:', err)
   *   });
   * ```
   */
  login(credentials: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(`${this.apiUrl}/login`, credentials).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.tokenService.setToken(response.data.access_token);
          this.userService.setCurrentUser(response.data.user);
          this.securityLog.logAuthEvent('login');
          this.startTokenRefresh();
          this.isAuthenticatedSubject.next(true);
        }
      })
    );
  }

  /**
   * Logs out the current user
   *
   * Clears the authentication token and user state.
   * Also makes an API call to invalidate the token on the server.
   *
   * @returns Observable containing the logout response
   *
   * @example
   * ```typescript
   * this.authService.logout().subscribe({
   *   next: () => {
   *     console.log('Logged out successfully');
   *     this.router.navigate(['/auth/login']);
   *   }
   * });
   * ```
   */
  logout(): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/logout`, {}).pipe(
      finalize(() => {
        this.securityLog.logAuthEvent('logout');
        this.clearAuthState();
      })
    );
  }

  /**
   * Refreshes the authentication token
   *
   * Obtains a new token before the current one expires.
   * Updates the stored token on success.
   *
   * @returns Observable containing the new token
   *
   * @example
   * ```typescript
   * this.authService.refreshToken().subscribe({
   *   next: (response) => console.log('Token refreshed'),
   *   error: (err) => console.error('Token refresh failed:', err)
   * });
   * ```
   */
  refreshToken(): Observable<ApiResponse<RefreshTokenResponse>> {
    const currentToken = this.tokenService.getToken();
    if (!currentToken) {
      throw new Error('No token available to refresh');
    }

    const request: RefreshTokenRequest = { token: currentToken };
    return this.http.post<ApiResponse<RefreshTokenResponse>>(`${this.apiUrl}/refresh`, request).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.tokenService.setToken(response.data.token);
          this.startTokenRefresh(); // Schedule next refresh
        }
      })
    );
  }

  /**
   * Initiates the forgot password flow
   *
   * Sends a password reset email to the specified email address.
   * Returns a generic success message for security purposes.
   *
   * @param email - Email address to send the reset link to
   * @returns Observable containing the response
   *
   * @example
   * ```typescript
   * this.authService.forgotPassword('admin@blueride.com').subscribe({
   *   next: (response) => console.log('Reset email sent'),
   *   error: (err) => console.error('Failed to send reset email:', err)
   * });
   * ```
   */
  forgotPassword(email: string): Observable<ApiResponse<void>> {
    const request: ForgotPasswordRequest = { email };
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/forgot-password`, request);
  }

  /**
   * Resets the user's password using a reset token
   *
   * Submits a new password along with the reset token received via email.
   *
   * @param data - Password reset request data
   * @returns Observable containing the response
   *
   * @example
   * ```typescript
   * const resetData = {
   *   email: 'admin@blueride.com',
   *   token: 'reset-token-from-email',
   *   password: 'NewPassword123!',
   *   password_confirmation: 'NewPassword123!'
   * };
   * this.authService.resetPassword(resetData).subscribe({
   *   next: () => {
   *     console.log('Password reset successful');
   *     this.router.navigate(['/auth/login']);
   *   },
   *   error: (err) => console.error('Password reset failed:', err)
   * });
   * ```
   */
  resetPassword(data: ResetPasswordRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/reset-password`, data);
  }

  /**
   * Checks if the user is currently authenticated
   *
   * Verifies that a valid token exists in storage.
   * Note: This does not verify if the token is still valid on the server.
   *
   * @returns True if a token exists, false otherwise
   *
   * @example
   * ```typescript
   * if (this.authService.isAuthenticated()) {
   *   console.log('User is authenticated');
   * } else {
   *   this.router.navigate(['/auth/login']);
   * }
   * ```
   */
  isAuthenticated(): boolean {
    return this.tokenService.hasToken();
  }
}
